import React, { Component } from "react";
import StoreOverrideForm from "../StoreOverrideForm";
import { forkJoin } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import { componentFromStream } from "recompose";
import withLoadingAndErrorHandling from "../../utils/reactive/wrapPageApiCall.js";
import apiProxy from "../../api/apiProxy";
import moment from "moment";
import { Breadcrumbs, BreadcrumbsItem } from "@sainsburys-tech/fable";
import PageTitle from "../PageTitle";
import { Link } from "react-router-dom";
import { Redirect } from "react-router-dom";
import { ToastConsumer } from "../WithToastManager";
import * as zoneTypeKeys from "../../model/lighting-zone-types/zoneTypeKeys.js";
import { OCCURS_ONCE } from "../../model/lighting-overrides/overrideFrequencyKeys";

class OverrideEdit extends Component {
  constructor(props) {
    super(props);

    const store = this.props.override.stores.map(store => {
      return {
        value: store.storeId,
        label: `${store.name} (${store.storeId})`
      };
    })[0];

    const startTime = moment(this.props.override.start).format("HH:mm");
    const endTime = moment(this.props.override.end).format("HH:mm");
    this.state = {
      initialValues: {
        overrideId: this.props.override.overrideId,
        overrideName: this.props.override.name,
        storeId: store.value,
        scene: this.props.override.scene.name,
        selectedZones: this.props.override.overrideAllZones
          ? []
          : this.props.override.zones,
        startTime: startTime,
        startDate: moment(this.props.override.start),
        endTime: endTime,
        endDate: moment(this.props.override.end),
        startDateFocused: false,
        endDateFocused: false,
        version: this.props.override.version,
        allZonesSelected: this.props.override.overrideAllZones,
        endTimeOptions: [],
        occurOnceOrRepeatDaily: OCCURS_ONCE,
        activeDays: [
          { name: "Monday", checked: false },
          { name: "Tuesday", checked: false },
          { name: "Wednesday", checked: false },
          { name: "Thursday", checked: false },
          { name: "Friday", checked: false },
          { name: "Saturday", checked: false },
          { name: "Sunday", checked: false }
        ]
      },
      zones: [],
      selectedStore: store,
      redirectToOverridesPage: false
    };
  }

  componentDidMount = () => {
    this.handleRetrieveZones(
      this.state.selectedStore.value,
      this.state.selectedStore
    ).then(() => {
      this.checkSelectedZonesExist();
    });
  };

  handleStoreDeselect = () => {
    this.setState({
      selectedStore: null,
      zones: []
    });
  };

  redirectToOverridesPage = () => {
    this.setState({ redirectToOverridesPage: true });
  };

  handleSaveComplete = () => {
    this.props.toastManager.add(
      "success",
      "Override configured",
      `Override has been configured successfully.`
    );
    this.redirectToOverridesPage();
  };

  handleSubmit = values => {
    const name = values.overrideName;
    const stores = [];
    stores.push(values.storeId);

    const scene = values.scene;
    const zones = [];

    if (!values.allZonesSelected) {
      values.selectedZones.map(zone => {
        return zones.push(zone.zoneId);
      });
    }

    const allZonesSelected = values.allZonesSelected;

    const startTime = values.startTime;

    const startDate = moment(values.startDate)
      .format()
      .substring(
        0,
        moment(values.startDate)
          .format()
          .indexOf("T")
      );

    const start = startDate + "T" + startTime + ":00";

    const endTime = values.endTime.split(" ")[0];

    const endDate = moment(values.endDate)
      .format()
      .substring(
        0,
        moment(values.endDate)
          .format()
          .indexOf("T")
      );

    const isRecurring = false;
    const daysOfWeek = [];

    const end = endDate + "T" + endTime + ":00";
    const version = values.version;
    return apiProxy.storeLighting.updateOverride(
      values.overrideId,
      name,
      stores,
      zones,
      allZonesSelected,
      scene,
      start,
      end,
      version,
      daysOfWeek,
      isRecurring
    );
  };

  checkSelectedZonesExist = () => {
    const selectedZones = this.state.initialValues.selectedZones;
    if (!selectedZones || selectedZones.length === 0) {
      return;
    }

    const zones = this.state.zones;
    const selectedZonesExist = selectedZones.every(selectedZone =>
      zones.find(z => z.zoneId === selectedZone.zoneId)
    );

    if (!selectedZonesExist) {
      this.clearZoneFields();
    }
  };

  clearZoneFields = () => {
    const initialValues = this.state.initialValues;
    initialValues.selectedZones = [];
    initialValues.allZonesSelected = false;
    this.setState({ initialValues: initialValues });
  };

  render() {
    if (this.state.redirectToOverridesPage) {
      return <Redirect to={`/lighting/overrides`} push={true} />;
    }
    return (
      <PageTitle title="Edit store override">
        <fieldset>
          <Breadcrumbs>
            <BreadcrumbsItem href="/lighting/overrides">
              Lighting overrides
            </BreadcrumbsItem>
            <BreadcrumbsItem href={`/lighting/overrides/${this.props.override.overrideId}/edit`}>
              Edit override
            </BreadcrumbsItem>
          </Breadcrumbs>

          <h2>Edit override</h2>
          <StoreOverrideForm
            handleStoreSelect={this.handleStoreSelect}
            handleDateChange={this.handleDateChange}
            handleDateFocus={this.handleDateFocus}
            startDate={moment(new Date())}
            startDateFocused={false}
            endDate={moment(new Date())}
            endDateFocused={true}
            initialValues={this.state.initialValues}
            zones={this.state.zones}
            handleRetrieveZones={this.handleRetrieveZones}
            selectedStore={this.state.selectedStore}
            scenes={this.props.scenes}
            onSubmit={this.handleSubmit}
            onSelect={this.handleOnSelect}
            handleStoreDeselect={this.handleStoreDeselect}
            onSaveComplete={this.handleSaveComplete}
            onCancel={this.redirectToOverridesPage}
          />
        </fieldset>
      </PageTitle>
    );
  }

  handleRetrieveZones = (storeId, selectedStore) => {
    return apiProxy.storeLighting.getStore(storeId).then(store => {
      this.setState({
        zones: mapZones(store.storeConfiguration),
        selectedStore: selectedStore
      });
    });
  };
}

const mapZones = zones => {
  return zones.zones
    .filter(f => f.zone.zoneType === zoneTypeKeys.CBUS)
    .map(zone => {
      return {
        zoneId: zone.zone.zoneId,
        name: zone.zone.name,
        zoneRef: zone.zone.zoneRef
      };
    });
};

const mapScenes = scenes => {
  if (scenes.scenes != null) {
    return scenes.scenes.map(scene => {
      return {
        value: scene.name,
        label: scene.description
      };
    });
  }
};

export default componentFromStream(props$ => {
  return withLoadingAndErrorHandling(() =>
    props$.pipe(
      mergeMap(({ getOverride, overrideId, getScenes }) =>
        forkJoin([getOverride(overrideId), getScenes()])
      ),
      map(([override, scenes]) => (
        <ToastConsumer>
          {toastManagerContext => (
            <OverrideEdit
              toastManager={toastManagerContext}
              override={override}
              scenes={mapScenes(scenes)}
            />
          )}
        </ToastConsumer>
      ))
    )
  );
});
